import { formatearMoneda, type CobroTarjetaVista, type EstadoCobroTarjeta } from '@barbones/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CreditCard, LoaderCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/api/cliente';
import { Button } from '@/components/ui/button';
import { Dialogo } from '@/components/ui/overlay';
import { avisarErrorCaja } from './datos';

/** Mensajes al cajero por resultado. El detalle técnico (`detalleError`) nunca se muestra. */
const MENSAJES: Record<Exclude<EstadoCobroTarjeta, 'pendiente' | 'aprobado'>, string> = {
  rechazado: 'La terminal rechazó la tarjeta. Los pagos capturados se conservan: intenta de nuevo o usa otra forma de pago.',
  expirado: 'Se agotó el tiempo en la terminal sin cobrar. Puedes volver a enviarlo o usar otra forma de pago.',
  cancelado: 'El cobro con tarjeta se canceló en la terminal.',
  error: 'No se pudo confirmar el cobro con Mercado Pago. Revisa la terminal antes de reintentar; el dueño recibió un aviso.',
};

function mensajeResultado(c: CobroTarjetaVista): string {
  if (c.estado === 'error' && c.detalleError?.startsWith('MP_CUENTA_REVOCADA')) {
    return 'La cuenta de Mercado Pago fue desconectada; pide al dueño que la vuelva a conectar.';
  }
  return MENSAJES[c.estado as keyof typeof MENSAJES];
}

const restante = (expiraEn: string) => Math.max(0, Math.ceil((new Date(expiraEn).getTime() - Date.now()) / 1000));
const reloj = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/**
 * Espera del cobro en la terminal Point. No se cierra con Escape ni clic fuera: solo termina
 * cuando el cobro se resuelve (aprobado → `onAprobado`; cualquier otro → mensaje y `onCerrar`).
 */
export function EsperaTerminalDialog({
  ventaId,
  cobroId,
  cobro: recibido,
  envio,
  onAprobado,
  onCerrar,
}: {
  ventaId: string;
  /** Cobro esperado; uno con otro id (respuesta vieja) se ignora. `null`: se acepta el que llegue. */
  cobroId: string | null;
  /** `null` mientras se envía a la terminal. */
  cobro: CobroTarjetaVista | null;
  envio?: { alias: string; montoMXN: number };
  onAprobado: (cobro: CobroTarjetaVista) => void;
  onCerrar: () => void;
}) {
  const qc = useQueryClient();
  // Se conserva el último cobro válido: una respuesta de otro cobro no cambia la espera.
  const [ultimoValido, setUltimoValido] = useState<CobroTarjetaVista | null>(null);
  const valido = recibido && (!cobroId || recibido.id === cobroId) ? recibido : null;
  if (valido && valido !== ultimoValido) setUltimoValido(valido);
  const cobro = recibido === null ? null : (valido ?? ultimoValido);
  const [segundos, setSegundos] = useState(() => (cobro ? restante(cobro.expiraEn) : null));
  const resuelto = useRef<string | null>(null);

  const expiraEn = cobro?.expiraEn;
  useEffect(() => {
    if (!expiraEn) return;
    setSegundos(restante(expiraEn));
    const t = setInterval(() => setSegundos(restante(expiraEn)), 1000);
    return () => clearInterval(t);
  }, [expiraEn]);

  useEffect(() => {
    if (!cobro || cobro.estado === 'pendiente') return;
    const clave = `${cobro.id}:${cobro.estado}`;
    if (resuelto.current === clave) return;
    resuelto.current = clave;
    if (cobro.estado === 'aprobado') onAprobado(cobro);
    else {
      toast.error(mensajeResultado(cobro));
      onCerrar();
    }
  }, [cobro, onAprobado, onCerrar]);

  const cancelar = useMutation({
    mutationFn: (id: string) => api<CobroTarjetaVista>(`/cobros-tarjeta/${id}/cancelar`, { method: 'POST' }),
    onSuccess: (c) => qc.setQueryData(['cobro-tarjeta', ventaId], c),
    // 502 MP_NO_DISPONIBLE: la orden aún puede cobrarse; 409 COBRO_APROBADO_SIN_REGISTRAR: ya se
    // aprobó y se está registrando. En ambos casos se avisa y se sigue esperando.
    onError: (e) => avisarErrorCaja(e),
  });

  const alias = cobro?.terminal.alias ?? envio?.alias ?? 'la terminal';
  const monto = cobro?.montoMXN ?? envio?.montoMXN ?? 0;
  const estado = !cobro ? 'Enviando…' : segundos === 0 ? 'Tiempo agotado; confirmando con Mercado Pago…' : `Esperando tarjeta en ${alias}`;

  return (
    <Dialogo abierto cerrable={false} onCambiarAbierto={() => undefined} titulo="Cobro con tarjeta" ancho="sm">
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="relative rounded-full bg-accent-soft p-4 text-accent">
          <CreditCard className="size-7" aria-hidden />
          <span className="absolute inset-0 rounded-full border-2 border-accent/40 motion-safe:animate-ping" aria-hidden />
        </div>
        <div className="w-full rounded-xl bg-primary px-4 py-3 text-on-primary">
          <p className="text-[12px] uppercase tracking-wider opacity-70">Monto en terminal</p>
          <p className="cifras font-display text-[30px] leading-tight">{formatearMoneda(monto)}</p>
        </div>
        <p role="status" aria-live="polite" className="flex items-center gap-2 text-[14px] font-medium">
          {!cobro && <LoaderCircle className="size-4 motion-safe:animate-spin" aria-hidden />}
          {estado}
        </p>
        {segundos !== null && (
          <p className="text-[12px] text-foreground-muted">
            Expira en <span className="cifras text-[20px] font-semibold text-foreground">{reloj(segundos)}</span>
          </p>
        )}
        <p className="text-[12.5px] text-foreground-muted">Pide al cliente que acerque, inserte o deslice su tarjeta en {alias}.</p>
        <Button
          variante="secundario"
          className="w-full"
          disabled={!cobro || cobro.estado !== 'pendiente'}
          cargando={cancelar.isPending}
          onClick={() => cobro && cancelar.mutate(cobro.id)}
        >
          Cancelar en terminal
        </Button>
      </div>
    </Dialogo>
  );
}
